import React from "react"
import { PDFSectionBorder } from "./PDF"
import { createLogoSize, PDFLogo } from "./PDFLogo"
import { PDFAsset } from "../models/PDFAsset"
import { PDFWorkOrder } from "../models/PDFWorkOrder"
import { PDFSection } from "./PDFSection"

/**
 * Action Item 6764: base pieces shared by the form-style PDFs (WorkOrderFormPDF and
 * PartsListFormPDF). New versions of the legacy PDF/PDFPageHeader/PDFHeader components:
 * the page header line sits at the very top of every printed page and the asset header
 * is compacted so the body content starts higher on the page.
 */

const style = `
    :root {
        --bg-color: #ffffff;
        --font-family: helvetica;
        --font-color: #000000;
        --font-size: 1.6rem;
    }

    *,
    *:before,
    *:after {
        box-sizing: inherit;
        margin: 0;
        padding: 0;
    }
    html {
        font-size: 62.5%;
        box-sizing: border-box;
    }

    body {
        font-size: var(--font-size);
        font-family: var(--font-family);
        color: var(--font-color);
        background: var(--bg-color);
        overflow: visible;
        -webkit-print-color-adjust: exact;
    }

    @page {
        size: 8.5in 11in;
        margin: 10px 25px 50px 25px;
    }

    .pdf-section:nth-child(n+1):nth-last-child(n+2) {
        margin-bottom: 12px;
    }
`

/**
 * Document root. The body is laid out inside a single-cell table whose header row holds
 * the "WO# / Company" line: the browser repeats the table header at the top of every
 * printed page, so the values carried forward between pages are retained even though
 * the body content flows freely across page breaks. Unlike a fixed-position header, a
 * table header stays scoped to its own document when several PDFs print together.
 */
export function FormPDF({
    pageHeaderTitle,
    companyName,
    children,
}: {
    pageHeaderTitle: string
    companyName: string
    children?: React.ReactNode
}): JSX.Element {
    return (
        <div className="pdf" style={{ breakBefore: "page" }}>
            <style>{style}</style>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <td>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: "20px",
                                    paddingBottom: "17px",
                                    fontSize: "1.4rem",
                                }}
                            >
                                <div>{pageHeaderTitle}</div>
                                <div>{companyName}</div>
                            </div>
                        </td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{children}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

export type FormPDFAssetHeaderProps = {
    title: string
    asset: PDFAsset
    workOrder?: PDFWorkOrder
    showSerialNumber?: boolean
    showLastKnownLocation?: boolean
    companyLogoUrl: string | null
}

/**
 * Asset information header. Compared to the legacy PDFHeader box, the Due Date and
 * Urgency lines are removed and the Due meters are replaced with the Physical meters.
 */
export function FormPDFAssetHeader({
    title,
    asset,
    workOrder,
    showSerialNumber = true,
    showLastKnownLocation = true,
    companyLogoUrl,
}: FormPDFAssetHeaderProps): JSX.Element {
    return (
        <>
            <PDFSection style={{ display: "grid", gap: "20px", gridTemplateColumns: "2fr auto", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <PDFLogo {...createLogoSize({ height: 65 })} companyLogoUrl={companyLogoUrl} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 4 }}>
                        <div style={{ fontWeight: 700, fontSize: "2.0rem" }}>{title}</div>
                        <div>{asset.label}</div>
                        {showSerialNumber && (
                            <div style={{ fontSize: ".9em" }}>
                                <strong>Serial Number/VIN:</strong> {asset.serialNumber}
                            </div>
                        )}
                    </div>
                </div>
                {workOrder && (
                    <div
                        style={{
                            border: PDFSectionBorder,
                            padding: 4,
                            display: "grid",
                            gap: "2px 32px",
                            gridTemplateColumns: "auto auto",
                            justifyContent: "space-between",
                            fontSize: "1.2rem",
                        }}
                    >
                        {workOrder.assignedToName && (
                            <>
                                <FormPDFAssetHeaderLabel>Assigned To</FormPDFAssetHeaderLabel>
                                <div>{workOrder.assignedToName}</div>
                            </>
                        )}

                        <FormPDFAssetHeaderLabel>Closed Date</FormPDFAssetHeaderLabel>
                        <div>{workOrder.closedDate ?? ""}</div>

                        <FormPDFAssetHeaderLabel>Physical Hour Meter</FormPDFAssetHeaderLabel>
                        <div>{workOrder.physicalHourMeter ?? ""}</div>

                        <FormPDFAssetHeaderLabel>Physical Odometer</FormPDFAssetHeaderLabel>
                        <div>{workOrder.physicalOdometer ?? ""}</div>
                    </div>
                )}
            </PDFSection>
            {asset.lastKnownLocation && showLastKnownLocation && (
                <PDFSection>
                    <div style={{ fontStyle: "italic", textAlign: "center", fontSize: ".9em" }}>
                        <strong>Last Known Location:</strong> {asset.lastKnownLocation}
                    </div>
                </PDFSection>
            )}
        </>
    )
}

function FormPDFAssetHeaderLabel({ children }: { children: React.ReactNode }): JSX.Element {
    return <div style={{ fontWeight: 700 }}>{children}</div>
}
